import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { DashboardCard } from "@dashboard/components/Card";
import { ListPageLayout } from "@dashboard/components/Layouts";
import ResponsiveTable from "@dashboard/components/ResponsiveTable";
import TableRowLink from "@dashboard/components/TableRowLink";
import { WsmFitmentQualifierFragment } from "@dashboard/graphql";
import useNavigator from "@dashboard/hooks/useNavigator";
import { renderCollection } from "@dashboard/misc";
import { TableBody, TableCell, TableHead } from "@material-ui/core";
import { Button, Skeleton, Text } from "@saleor/macaw-ui-next";
import React from "react";
import { useIntl } from "react-intl";

import { qualifierMessages, sectionMessages } from "../messages";
import { wsmFitmentQualifierAddPath, wsmFitmentQualifierUrl } from "../urls";
import { WsmFitmentLoadError } from "./WsmFitmentLoadError";
import { WsmFitmentSectionLinks } from "./WsmFitmentSectionLinks";

const numberOfColumns = 3;

export interface WsmFitmentQualifierListPageProps {
  qualifiers: WsmFitmentQualifierFragment[] | undefined;
  /** Set when the list failed to load: the card shows a retry instead of rows. */
  onRetry?: () => void;
}

export const WsmFitmentQualifierListPage = ({
  qualifiers,
  onRetry,
}: WsmFitmentQualifierListPageProps) => {
  const intl = useIntl();
  const navigate = useNavigator();

  return (
    <ListPageLayout>
      <TopNav title={intl.formatMessage(sectionMessages.qualifiers)}>
        <WsmFitmentSectionLinks current="qualifiers" />
        <Button
          onClick={() => navigate(wsmFitmentQualifierAddPath)}
          data-test-id="wsm-fitment-qualifier-create"
        >
          {intl.formatMessage(qualifierMessages.create)}
        </Button>
      </TopNav>
      <DashboardCard data-test-id="wsm-fitment-qualifier-list">
        <DashboardCard.Content paddingX={0}>
          {onRetry ? (
            <WsmFitmentLoadError onRetry={onRetry} />
          ) : (
            <ResponsiveTable>
              <TableHead>
                <TableRowLink>
                  <TableCell>{intl.formatMessage(qualifierMessages.colName)}</TableCell>
                  <TableCell>{intl.formatMessage(qualifierMessages.colDisplayLabel)}</TableCell>
                  <TableCell>{intl.formatMessage(qualifierMessages.colValues)}</TableCell>
                </TableRowLink>
              </TableHead>
              <TableBody>
                {renderCollection(
                  qualifiers,
                  row => (
                    <TableRowLink
                      key={row ? row.id : "skeleton"}
                      hover={!!row}
                      href={row ? wsmFitmentQualifierUrl(row.id) : undefined}
                      data-test-id="wsm-fitment-qualifier-row"
                    >
                      <TableCell>{row ? row.name : <Skeleton />}</TableCell>
                      <TableCell>{row ? row.displayLabel || "-" : <Skeleton />}</TableCell>
                      <TableCell>
                        {row ? (
                          row.values.map(value => value.value).join(", ") || "-"
                        ) : (
                          <Skeleton />
                        )}
                      </TableCell>
                    </TableRowLink>
                  ),
                  () => (
                    <TableRowLink data-test-id="wsm-fitment-qualifier-list-empty">
                      <TableCell colSpan={numberOfColumns}>
                        <Text size={3} color="default2">
                          {intl.formatMessage(qualifierMessages.empty)}
                        </Text>
                      </TableCell>
                    </TableRowLink>
                  ),
                )}
              </TableBody>
            </ResponsiveTable>
          )}
        </DashboardCard.Content>
      </DashboardCard>
    </ListPageLayout>
  );
};

export default WsmFitmentQualifierListPage;
